import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, BarChart3, FlaskConical, LayoutDashboard, History as HistoryIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const navLinkClass = ({ isActive }) =>
  `inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md transition-colors duration-200 ${
    isActive
      ? "bg-slate-100 text-slate-900"
      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
  }`;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-body">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <Logo />
          </Link>

          {user && typeof user === "object" && (
            <nav className="hidden md:flex items-center gap-1">
              <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4" /> Início
              </NavLink>
              <NavLink to="/anova" className={navLinkClass} data-testid="nav-anova">
                <BarChart3 className="w-4 h-4" /> Testes com ANOVA
              </NavLink>
              <NavLink to="/sensorial" className={navLinkClass} data-testid="nav-sensorial">
                <FlaskConical className="w-4 h-4" /> Testes de Escolha Forçada
              </NavLink>
              <NavLink to="/historico" className={navLinkClass} data-testid="nav-historico">
                <HistoryIcon className="w-4 h-4" /> Histórico
              </NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin" className={navLinkClass} data-testid="nav-admin">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </NavLink>
              )}
            </nav>
          )}

          <div className="flex items-center gap-3">
            {user && typeof user === "object" ? (
              <>
                <div className="hidden sm:flex flex-col items-end leading-tight">
                  <span className="text-sm font-semibold text-slate-900" data-testid="header-user-name">
                    {user.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {user.role === "admin" ? "Administrador" : "Usuário"}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="border-slate-300"
                >
                  <LogOut className="w-4 h-4 mr-1" /> Sair
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">{children}</main>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Sensorize STAT — Análise Estatística Sensorial</span>
          <span>v1.0.0</span>
        </div>
      </footer>
    </div>
  );
}
